/**
 * 애플리케이션 라우트 경로 설정
 *
 * 미들웨어 및 다른 곳에서 공통으로 사용되는 경로 정의를 중앙 관리합니다.
 */

/**
 * 공개 경로: 인증 없이 접근 가능한 경로
 */
export const PUBLIC_PATHS = [
  '/login',
  '/auth',
  '/invite',
  '/signup',
  '/policy',
  '/welcome', // Welcome 페이지: 인증된 사용자가 프로필 생성하는 페이지
  '/events/ugcevent', // UGC Event: 누구나 접근 가능한 이벤트 랜딩 페이지
  '/eval', // 브랜드 매치 평가 (공개)
] as const;

/**
 * 특수 공개 경로: 공개 경로이지만 인증된 사용자도 접근 가능한 경로
 */
export const SPECIAL_PUBLIC_PATHS = {
  /**
   * 인증 콜백 경로: OAuth 콜백 등 인증된 사용자도 접근해야 하는 경로
   * (예: TikTok 계정 연동 콜백)
   */
  AUTH: '/auth',

  /**
   * Welcome 페이지: 인증된 사용자가 프로필 생성/TikTok 연동 완료하는 페이지
   * TikTok 연동이 완료되지 않은 사용자도 반드시 접근해야 함
   */
  WELCOME: '/welcome',

  /**
   * UGC Event: 누구나 접근 가능한 이벤트 랜딩 페이지
   * 로그인/비로그인 모두 접근 허용
   */
  UGC_EVENT: '/events/ugcevent',

  /**
   * 브랜드 매치 평가: 토큰 기반 공개 평가 페이지
   * 로그인/비로그인 모두 접근 허용
   */
  EVAL: '/eval',
} as const;

/**
 * 홈 경로: 로그인 성공 후 기본 리다이렉트 경로
 */
export const HOME_PATH = '/';

/**
 * 경로가 특수 공개 경로인지 확인
 * @param pathname - 확인할 경로
 * @returns 특수 공개 경로인 경우 해당 키, 아닌 경우 null
 */
export function isSpecialPublicPath(pathname: string): keyof typeof SPECIAL_PUBLIC_PATHS | null {
  const normalizedPath = normalizePath(pathname);

  for (const [key, path] of Object.entries(SPECIAL_PUBLIC_PATHS)) {
    if (normalizedPath.startsWith(path)) {
      return key as keyof typeof SPECIAL_PUBLIC_PATHS;
    }
  }

  return null;
}

/**
 * 경로 정규화: 끝의 슬래시 제거
 */
function normalizePath(path: string): string {
  if (path === '/') {
    return '/';
  }
  return path.endsWith('/') ? path.slice(0, -1) : path;
}

/**
 * 경로가 공개 경로인지 확인
 * @param pathname - 확인할 경로
 * @returns 공개 경로인 경우 true
 */
export function isPublicPath(pathname: string): boolean {
  const normalizedPath = normalizePath(pathname);
  return PUBLIC_PATHS.some(path => normalizedPath.startsWith(path));
}

/**
 * 경로가 특정 특수 공개 경로인지 확인
 * @param pathname - 확인할 경로
 * @param specialPath - 확인할 특수 경로 키
 * @returns 해당 특수 경로인 경우 true
 */
export function isSpecificSpecialPath(
  pathname: string,
  specialPath: keyof typeof SPECIAL_PUBLIC_PATHS,
): boolean {
  const normalizedPath = normalizePath(pathname);
  const targetPath = SPECIAL_PUBLIC_PATHS[specialPath];
  return normalizedPath.startsWith(targetPath);
}
